import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, put, post, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/common/Page";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { AddressInput } from "@/components/common/AddressInput";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Settings, History, Save, MapPin, Sparkles, Database, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const FormRow = ({ label: l, children, hint, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-xs">{l}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const FIELDS = ["company_name", "company_org_number", "company_address", "company_phone", "company_email", "depot_address", "depot_lat", "depot_lng", "margin_threshold_pct", "margin_approval_required", "quote_valid_days", "vat_pct", "planning_buffer_days", "default_terms", "notify_quote_expiry_days", "notify_project_start_days"];

const ENTITY_TYPES = ["lead", "customer", "quote", "calculation", "project", "module", "module_type", "allocation", "transport", "service_job", "issue", "task", "supplier", "document", "rate", "user", "settings", "system"];
const ACTION_TONE = { create: "bg-primary/10 text-primary", update: "bg-muted text-foreground", delete: "bg-rose-50 text-rose-700", status: "bg-amber-50 text-amber-700", login: "bg-muted text-foreground" };

const AuditLog = () => {
  const [entityType, setEntityType] = useState("all");
  const [q, setQ] = useState("");
  const audit = useQuery({ queryKey: ["audit", entityType], queryFn: () => get("/audit", { limit: 300, ...(entityType !== "all" ? { entity_type: entityType } : {}) }) });
  const rows = useMemo(() => (audit.data || []).filter((a) => !q || `${a.summary || ""} ${a.user_name || ""} ${a.entity_type} ${a.action}`.toLowerCase().includes(q.toLowerCase())), [audit.data, q]);
  return (
    <div className="space-y-3" data-testid="audit-log">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-48 h-9" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Alle objekttyper</SelectItem>
            {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i logg" className="h-9 w-56" data-testid="audit-search-input" />
        <Button variant="ghost" size="sm" className="h-9" onClick={() => audit.refetch()} data-testid="audit-refresh-button"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Oppdater</Button>
        <div className="text-xs text-muted-foreground ml-auto tabular">{rows.length} hendelser</div>
      </div>
      {audit.isLoading ? <LoadingState /> : audit.error ? <ErrorState error={audit.error} onRetry={audit.refetch} /> : rows.length === 0 ? <EmptyState title="Ingen hendelser" description="Revisjonsloggen er tom for gjeldende filter." /> : (
        <div className="rounded-md border bg-card divide-y">
          {rows.map((a) => (
            <div key={a.id} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4" data-testid={`audit-row-${a.id}`}>
              <div className="text-xs text-muted-foreground tabular sm:w-36 shrink-0">{fmtDateTime(a.timestamp || a.created_at || a.at)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="font-mono text-[10px]">{a.entity_type}</Badge>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${ACTION_TONE[a.action] || "bg-muted"}`}>{a.action}</span>
                  <span className="text-sm">{a.summary || "–"}</span>
                </div>
                {(a.changes || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    {a.changes.slice(0, 6).map((c, i) => (
                      <span key={i} className="font-mono">{c.field}: <span className="line-through opacity-70">{String(c.old ?? "–").slice(0, 30)}</span> → {String(c.new ?? "–").slice(0, 30)}</span>
                    ))}
                    {a.changes.length > 6 && <span>+{a.changes.length - 6} felt</span>}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground sm:w-36 shrink-0 sm:text-right">{a.user_name || "System"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SettingsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("innstillinger");
  const [form, setForm] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => get("/settings") });

  useEffect(() => {
    if (settings.data && !form) setForm(Object.fromEntries(FIELDS.map((k) => [k, settings.data[k] ?? (k === "margin_approval_required" ? true : "")])));
  }, [settings.data, form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = useMemo(() => form && settings.data && FIELDS.some((k) => String(form[k] ?? "") !== String(settings.data[k] ?? "")), [form, settings.data]);

  const save = useMutation({
    mutationFn: () => {
      const numKeys = ["depot_lat", "depot_lng", "margin_threshold_pct", "vat_pct"];
      const intKeys = ["quote_valid_days", "planning_buffer_days", "notify_quote_expiry_days", "notify_project_start_days"];
      const body = {};
      FIELDS.forEach((k) => {
        const v = form[k];
        if (numKeys.includes(k)) body[k] = v === "" || v === null ? null : Number(v);
        else if (intKeys.includes(k)) body[k] = v === "" || v === null ? null : parseInt(v, 10);
        else if (k === "margin_approval_required") body[k] = !!v;
        else body[k] = v === "" ? null : v;
      });
      return put("/settings", body);
    },
    onSuccess: () => { toast.success("Innstillinger lagret"); qc.invalidateQueries({ queryKey: ["settings"] }); setForm(null); },
    onError: (e) => toastError(e, "Kunne ikke lagre innstillinger"),
  });

  const reseed = useMutation({
    mutationFn: () => post("/admin/seed/reset"),
    onSuccess: () => { toast.success("Demodata tilbakestilt"); qc.invalidateQueries(); setConfirmReset(false); },
    onError: (e) => { toastError(e, "Kunne ikke tilbakestille"); setConfirmReset(false); },
  });

  const geocodeDepot = useMutation({
    mutationFn: () => get("/maps/geocode", { address: form.depot_address }),
    onSuccess: (g) => { if (g?.lat) { set("depot_lat", g.lat); set("depot_lng", g.lng); toast.success("Depot-koordinater oppdatert"); } else toast.info("Fant ingen koordinater"); },
    onError: (e) => toastError(e, "Geokoding feilet"),
  });

  const readOnly = !can("users:write");

  return (
    <div className="page-pad space-y-4 fade-up" data-testid="settings-page">
      <PageHeader
        title="Systeminnstillinger"
        subtitle="Firmaopplysninger, kalkyleregler, varsler, integrasjoner og revisjonslogg"
        actions={tab === "innstillinger" && !readOnly && <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="settings-save-button"><Save className="h-4 w-4 mr-1" /> {save.isPending ? "Lagrer…" : "Lagre endringer"}</Button>}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-9" data-testid="settings-tabs">
          <TabsTrigger value="innstillinger" data-testid="settings-tab-innstillinger"><Settings className="h-4 w-4 mr-1" /> Innstillinger</TabsTrigger>
          <TabsTrigger value="revisjon" data-testid="settings-tab-revisjon"><History className="h-4 w-4 mr-1" /> Revisjonslogg</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "revisjon" ? <AuditLog /> : settings.isLoading || !form ? <LoadingState /> : settings.error ? <ErrorState error={settings.error} onRetry={settings.refetch} /> : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <Section title="Firma" description="Vises på tilbud, PDF og offentlig skjema">
              <fieldset disabled={readOnly} className="grid gap-3 sm:grid-cols-2">
                <FormRow label="Firmanavn"><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="settings-company-name" /></FormRow>
                <FormRow label="Organisasjonsnummer"><Input value={form.company_org_number || ""} onChange={(e) => set("company_org_number", e.target.value)} className="font-mono" data-testid="settings-org-number" /></FormRow>
                <FormRow label="Adresse" className="sm:col-span-2"><Input value={form.company_address || ""} onChange={(e) => set("company_address", e.target.value)} data-testid="settings-company-address" /></FormRow>
                <FormRow label="Telefon"><Input value={form.company_phone || ""} onChange={(e) => set("company_phone", e.target.value)} data-testid="settings-company-phone" /></FormRow>
                <FormRow label="E-post"><Input type="email" value={form.company_email || ""} onChange={(e) => set("company_email", e.target.value)} data-testid="settings-company-email" /></FormRow>
              </fieldset>
            </Section>

            <Section title="Depot og transport" description="Utgangspunkt for kjørerute, avstand og ferjeberegning">
              <fieldset disabled={readOnly} className="space-y-3">
                <FormRow label="Depotadresse">
                  <div className="flex gap-2">
                    <div className="flex-1"><AddressInput value={form.depot_address || ""} onChange={(v) => set("depot_address", v)} onSelect={({ address }) => set("depot_address", address)} placeholder="Adresse til modul-lager" testId="settings-depot-address" /></div>
                    <Button type="button" variant="outline" onClick={() => geocodeDepot.mutate()} disabled={!form.depot_address || geocodeDepot.isPending || !settings.data?.maps_enabled} data-testid="settings-geocode-button"><MapPin className="h-4 w-4 mr-1" /> Finn koordinater</Button>
                  </div>
                </FormRow>
                <div className="grid grid-cols-2 gap-3">
                  <FormRow label="Breddegrad"><Input type="number" step="0.000001" value={form.depot_lat ?? ""} onChange={(e) => set("depot_lat", e.target.value)} className="font-mono" data-testid="settings-depot-lat" /></FormRow>
                  <FormRow label="Lengdegrad"><Input type="number" step="0.000001" value={form.depot_lng ?? ""} onChange={(e) => set("depot_lng", e.target.value)} className="font-mono" data-testid="settings-depot-lng" /></FormRow>
                </div>
                <FormRow label="Planleggingsbuffer (dager)" hint="Legges til mellom prosjekter på samme modul for klargjøring og transport">
                  <Input type="number" min="0" value={form.planning_buffer_days ?? ""} onChange={(e) => set("planning_buffer_days", e.target.value)} className="w-32 tabular" data-testid="settings-buffer-days" />
                </FormRow>
              </fieldset>
            </Section>

            <Section title="Kalkyle og tilbud" description="Regler som styrer godkjenning og gyldighet">
              <fieldset disabled={readOnly} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <FormRow label="Marginterskel %" hint="Kalkyler under terskelen krever godkjenning"><Input type="number" step="0.5" value={form.margin_threshold_pct ?? ""} onChange={(e) => set("margin_threshold_pct", e.target.value)} className="tabular" data-testid="settings-margin-threshold" /></FormRow>
                  <FormRow label="Tilbud gyldig (dager)"><Input type="number" min="1" value={form.quote_valid_days ?? ""} onChange={(e) => set("quote_valid_days", e.target.value)} className="tabular" data-testid="settings-quote-valid-days" /></FormRow>
                  <FormRow label="MVA %"><Input type="number" step="0.5" value={form.vat_pct ?? ""} onChange={(e) => set("vat_pct", e.target.value)} className="tabular" data-testid="settings-vat" /></FormRow>
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                  <div><div className="text-sm font-medium">Krev godkjenning under marginterskel</div><div className="text-xs text-muted-foreground">Ledelse må godkjenne kalkylen før tilbudet kan sendes</div></div>
                  <Switch checked={!!form.margin_approval_required} onCheckedChange={(v) => set("margin_approval_required", v)} data-testid="settings-margin-approval-switch" />
                </div>
                <FormRow label="Standard betingelser" hint="Vises nederst i tilbuds-PDF">
                  <Textarea rows={5} value={form.default_terms || ""} onChange={(e) => set("default_terms", e.target.value)} data-testid="settings-default-terms" />
                </FormRow>
              </fieldset>
            </Section>

            <Section title="Varsler" description="Når systemet skal varsle salg og prosjektledere">
              <fieldset disabled={readOnly} className="grid gap-3 sm:grid-cols-2">
                <FormRow label="Tilbud utløper om (dager)"><Input type="number" min="0" value={form.notify_quote_expiry_days ?? ""} onChange={(e) => set("notify_quote_expiry_days", e.target.value)} className="tabular w-32" data-testid="settings-notify-quote" /></FormRow>
                <FormRow label="Prosjektstart om (dager)"><Input type="number" min="0" value={form.notify_project_start_days ?? ""} onChange={(e) => set("notify_project_start_days", e.target.value)} className="tabular w-32" data-testid="settings-notify-project" /></FormRow>
              </fieldset>
            </Section>
          </div>

          <div className="space-y-4">
            <Section title="Integrasjoner">
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3" data-testid="integration-maps">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1"><div className="font-medium">Google Maps Platform</div><div className="text-xs text-muted-foreground">Adressesøk, geokoding, kjørerute og ferje</div></div>
                  {settings.data?.maps_enabled ? <StatusBadge status="aktiv" /> : <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" aria-hidden />Ikke konfigurert</span>}
                </div>
                <div className="flex items-start gap-3" data-testid="integration-ai">
                  <Sparkles className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1"><div className="font-medium">AI-assistent</div><div className="text-xs text-muted-foreground">Løsningsforslag, tekstutkast og spørsmål mot driftsdata</div></div>
                  {settings.data?.ai_enabled ? <StatusBadge status="aktiv" /> : <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" aria-hidden />Ikke konfigurert</span>}
                </div>
                <p className="text-[11px] text-muted-foreground">API-nøkler settes som miljøvariabler på serveren (GOOGLE_MAPS_API_KEY, EMERGENT_LLM_KEY) og eksponeres aldri i nettleseren.</p>
              </div>
            </Section>
            {!readOnly && (
              <Section title="Demodata" description="Kun for test og opplæring">
                <div className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">Tilbakestiller alle kunder, henvendelser, prosjekter, moduler, tilbud og driftsdata til realistisk demodata. Brukere beholdes.</p>
                  <Button variant="outline" className="w-full" onClick={() => setConfirmReset(true)} data-testid="settings-reseed-button"><Database className="h-4 w-4 mr-1" /> Tilbakestill demodata</Button>
                </div>
              </Section>
            )}
            <Section title="Sist oppdatert">
              <div className="text-xs text-muted-foreground">{settings.data?.updated_at ? `${fmtDateTime(settings.data.updated_at)} av ${settings.data.updated_by_name || "–"}` : "Standardverdier"}</div>
            </Section>
          </div>
        </div>
      )}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="confirm-reseed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tilbakestille demodata?</AlertDialogTitle>
            <AlertDialogDescription>Alle endringer i kunder, prosjekter, moduler, tilbud og drift slettes og erstattes med demodata. Handlingen logges i revisjonsloggen og kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => reseed.mutate()} disabled={reseed.isPending} data-testid="confirm-reseed-button">{reseed.isPending ? "Tilbakestiller…" : "Ja, tilbakestill"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
